import { Provider, MfaVerificationMethod } from '@arc-connect/schema';
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Label,
  Switch,
} from '@arcadiapower/design-system';
import { useController, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ContentSectionFullHeight, Footer } from '@client/components/connect-container';
import { Form } from '@client/components/form';
import { parseErrorMessage } from '@client/config/errors';
import { ty } from '@client/styles/typography';
import { Fields, OptOutRow, OptOutCopy } from './mfa-verification-code-form.style';

export type Props = {
  selectedVerificationMethod?: MfaVerificationMethod;
  onSubmit: ({
    verificationCode,
    isMfaOptOut,
  }: {
    verificationCode: string;
    isMfaOptOut: boolean;
  }) => void;
  onMethodReselect: () => void;
  onResendCode: () => void;
  provider: Provider;
  pending: boolean;
  submissionError?: unknown;
  isResubmission: boolean;
  canReselectMethod: boolean;
};

export const MfaVerificationCodeForm = ({
  selectedVerificationMethod,
  onSubmit,
  onMethodReselect,
  onResendCode,
  provider,
  pending,
  submissionError,
  isResubmission,
  canReselectMethod,
}: Props): JSX.Element => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.mfaVerificationCodeForm' });
  const { control, handleSubmit } = useForm({
    defaultValues: { verificationCode: '', isMfaOptOut: false },
  });
  const { field, fieldState } = useController({
    name: 'verificationCode',
    control,
    rules: {
      required: t('validations.fieldRequiredErrorMessage', {
        fieldName: t('inputs.verificationCode'),
      }),
    },
  });
  const { field: isMfaOptOutField } = useController({
    name: 'isMfaOptOut',
    control,
  });
  const getResubmissionError = () => {
    return isResubmission ? t('errors.invalidCode') : undefined;
  };
  const verificationCodeError = fieldState.error?.message ?? getResubmissionError();
  const showMfaOptOut = provider.multiFactorAuthentication.isOptOutSupported;
  return (
    <>
      <ContentSectionFullHeight centerContentVertically data-testid="mfa-verification-code-form">
        <p className={ty('default')} style={{ marginBottom: '16px' }}>
          <Trans
            i18nKey={`components.mfaVerificationCodeForm.description.${
              selectedVerificationMethod?.name
                ? selectedVerificationMethod?.type ?? 'other'
                : 'methodNameUndefined'
            }`}
            values={{
              providerName: provider.name,
              verificationMethod: selectedVerificationMethod?.name,
            }}
            components={{
              bold: <span style={{ fontWeight: 'bolder' }} />,
            }}
          />
        </p>
        <p className={ty('default')} style={{ marginBottom: '32px' }}>
          {t('description.enterCode')}
        </p>
        <Form onSubmit={handleSubmit(data => onSubmit(data))} id="mfaVerificationCodeForm">
          <Fields>
            <Field data-invalid={verificationCodeError ? true : undefined}>
              <FieldLabel htmlFor={field.name}>{t('inputs.verificationCode')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.value ?? ''}
                onBlur={field.onBlur}
                onChange={event => field.onChange(event.target.value)}
                aria-invalid={verificationCodeError ? true : undefined}
              />
              {verificationCodeError ? <FieldError>{verificationCodeError}</FieldError> : null}
            </Field>
            {showMfaOptOut ? (
              <OptOutRow>
                <Switch
                  id="mfa-opt-out"
                  name={isMfaOptOutField.name}
                  checked={isMfaOptOutField.value}
                  onCheckedChange={isMfaOptOutField.onChange}
                  data-testid="mfa-opt-out-switch"
                />
                <OptOutCopy>
                  <Label htmlFor="mfa-opt-out">{t('inputs.mfaOptOut')}</Label>
                  <p className={ty('paragraph400')}>{t('description.mfaOptOut')}</p>
                </OptOutCopy>
              </OptOutRow>
            ) : null}
            {!!submissionError && <FieldError>{parseErrorMessage(submissionError)}</FieldError>}
            <p className={ty('paragraph400')} style={{ marginTop: '16px' }}>
              {t('resendCode.description')}
              <span className={ty('paragraph400')} style={{ marginLeft: '4px' }}>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto rounded-sm p-0"
                  onClick={onResendCode}
                >
                  {t('resendCode.link')}
                </Button>
              </span>
            </p>
            {!!canReselectMethod && (
              <p className={ty('paragraph400')}>
                {t('reselectMethod.description')}
                <span className={ty('paragraph400')} style={{ marginLeft: '4px' }}>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto rounded-sm p-0"
                    onClick={onMethodReselect}
                  >
                    {t('reselectMethod.link')}
                  </Button>
                </span>
              </p>
            )}
          </Fields>
        </Form>
      </ContentSectionFullHeight>
      <Footer>
        <Button
          type="submit"
          loading={pending}
          className="w-full"
          form="mfaVerificationCodeForm"
          aria-label={t('inputs.submitButton')}
          variant="inverse"
        >
          {t('inputs.submitButton')}
        </Button>
      </Footer>
    </>
  );
};
